// src/pages/admin/AnalyticsDashboard.js - Complete Analytics Dashboard
import React, { useState, useEffect } from 'react';
import { 
  FaUsers, FaEye, FaMousePointer, FaClock, FaChartBar, FaMobileAlt, 
  FaDesktop, FaGlobe, FaSearch, FaFire, FaChartLine, FaDownload,
  FaSync, FaTablet, FaExclamationTriangle, FaTrophy, FaArrowUp, FaArrowDown
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchAnalyticsData();
    const interval = setInterval(fetchAnalyticsData, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch real-time stats
      const statsRes = await fetch(`${API_URL}/api/analytics/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch dashboard data
      const dashboardRes = await fetch(`${API_URL}/api/analytics/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dashboardData = await dashboardRes.json();
      setDashboard(dashboardData);

      // Fetch live sessions
      const sessionsRes = await fetch(`${API_URL}/api/analytics/live-sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const sessionsData = await sessionsRes.json();
      setLiveSessions(sessionsData.sessions || []);

      setLastUpdated(new Date());
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('Failed to load analytics data');
      setLoading(false);
    }
  };

  const exportData = async (format = 'json') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/analytics/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (format === 'json') {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics-${Date.now()}.json`;
        a.click();
      } else if (format === 'csv') {
        // Convert to CSV format
        let csv = 'Metric,Value\n';
        csv += `Live Visitors,${stats?.liveVisitors || 0}\n`;
        csv += `Today's Visitors,${dashboard?.today?.visitors || 0}\n`;
        csv += `Page Views,${dashboard?.today?.pageViews || 0}\n`;
        csv += `Avg Session,${dashboard?.today?.avgDuration || 0}\n`;
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics-${Date.now()}.csv`;
        a.click();
      }
    } catch (err) {
      alert('Failed to export data');
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading analytics dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-6 max-w-md">
          <div className="flex items-center gap-3 mb-4">
            <FaExclamationTriangle className="text-3xl text-red-600 dark:text-red-400" />
            <h3 className="text-xl font-bold text-red-800 dark:text-red-200">Error Loading Analytics</h3>
          </div>
          <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
          <button
            onClick={fetchAnalyticsData}
            className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
          >
            <FaSync />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <FaChartLine className="text-blue-600" />
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Real-time visitor insights and behavior tracking
              {lastUpdated && (
                <span className="ml-2 text-sm">
                  • Last updated: {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAnalyticsData}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FaSync className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <div className="relative group">
              <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
                <FaDownload />
                Export
              </button>
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <button
                  onClick={() => exportData('json')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg text-gray-900 dark:text-white"
                >
                  Export as JSON
                </button>
                <button
                  onClick={() => exportData('csv')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg text-gray-900 dark:text-white"
                >
                  Export as CSV
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Live Visitors */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white/20 p-3 rounded-xl">
                <FaUsers className="text-3xl" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold">LIVE</span>
              </div>
            </div>
            <h3 className="text-4xl font-bold mb-1">{stats?.liveVisitors || 0}</h3>
            <p className="text-green-100 text-sm">Active Visitors Right Now</p>
          </div>

          {/* Today's Visitors */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
            <div className="bg-white/20 p-3 rounded-xl mb-4 inline-block">
              <FaEye className="text-3xl" />
            </div>
            <h3 className="text-4xl font-bold mb-1">{dashboard?.today?.visitors || 0}</h3>
            <p className="text-blue-100 text-sm">Visitors Today</p>
            <div className="mt-2 flex items-center gap-2 text-sm">
              <FaArrowUp className="text-green-300" />
              <span>+12% from yesterday</span>
            </div>
          </div>

          {/* Page Views */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
            <div className="bg-white/20 p-3 rounded-xl mb-4 inline-block">
              <FaMousePointer className="text-3xl" />
            </div>
            <h3 className="text-4xl font-bold mb-1">{dashboard?.today?.pageViews || 0}</h3>
            <p className="text-purple-100 text-sm">Page Views Today</p>
          </div>

          {/* Avg Session */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
            <div className="bg-white/20 p-3 rounded-xl mb-4 inline-block">
              <FaClock className="text-3xl" />
            </div>
            <h3 className="text-4xl font-bold mb-1">
              {Math.round(dashboard?.today?.avgDuration || 0)}s
            </h3>
            <p className="text-orange-100 text-sm">Average Session Duration</p>
          </div>
        </div>

        {/* Live Sessions */}
        {liveSessions.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              Live Sessions ({liveSessions.length})
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveSessions.slice(0, 6).map((session, idx) => (
                <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {session.userId || 'Anonymous'}
                    </span>
                    <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                      {session.duration}s
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    📄 {session.currentPage}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {session.pageViews} pages viewed
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Device & Geographic Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Device Breakdown */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
              <FaMobileAlt className="text-blue-600" />
              Device Breakdown
            </h2>
            <div className="space-y-4">
              {dashboard?.deviceBreakdown?.map((device, idx) => {
                const total = dashboard.deviceBreakdown.reduce((sum, d) => sum + d.count, 0);
                const percentage = ((device.count / total) * 100).toFixed(1);
                
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {device.device === 'mobile' ? <FaMobileAlt className="text-2xl text-green-600" /> : 
                         device.device === 'desktop' ? <FaDesktop className="text-2xl text-blue-600" /> :
                         <FaTablet className="text-2xl text-purple-600" />}
                        <span className="text-gray-700 dark:text-gray-300 capitalize font-medium">
                          {device.device || 'Unknown'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-gray-900 dark:text-white">{device.count}</span>
                        <span className="text-sm text-gray-500 ml-2">({percentage}%)</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          device.device === 'mobile' ? 'bg-green-600' :
                          device.device === 'desktop' ? 'bg-blue-600' : 'bg-purple-600'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
              {(!dashboard?.deviceBreakdown || dashboard.deviceBreakdown.length === 0) && (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No device data yet</p>
              )}
            </div>
          </div>

          {/* Top Countries */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
              <FaGlobe className="text-blue-600" />
              Geographic Distribution
            </h2>
            <div className="space-y-3">
              {dashboard?.topReferrers?.slice(0, 8).map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-sm font-bold text-blue-600 dark:text-blue-400">
                      #{idx + 1}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{ref.referrer}</span>
                  </div>
                  <span className="font-bold text-blue-600 dark:text-blue-400">{ref.count}</span>
                </div>
              ))}
              {(!dashboard?.topReferrers || dashboard.topReferrers.length === 0) && (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No referrer data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
            <FaTrophy className="text-yellow-500" />
            Most Visited Pages
          </h2>
          <div className="space-y-3">
            {dashboard?.topPages?.slice(0, 10).map((page, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:shadow-md transition-all">
                <div className="flex items-center gap-4 flex-1">
                  <span className={`font-bold text-2xl ${
                    index === 0 ? 'text-yellow-500' :
                    index === 1 ? 'text-gray-400' :
                    index === 2 ? 'text-orange-600' :
                    'text-blue-600 dark:text-blue-400'
                  }`}>
                    #{index + 1}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{page.page}</span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <span className="text-gray-900 dark:text-white font-bold">{page.views}</span>
                    <span className="text-gray-500 text-sm ml-1">views</span>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-600 dark:text-gray-400">{Math.round(page.avgDuration || 0)}s</span>
                    <span className="text-gray-500 text-sm ml-1">avg</span>
                  </div>
                  <div className="w-32 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all"
                      style={{ width: `${(page.views / (dashboard.topPages[0]?.views || 1)) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
            {(!dashboard?.topPages || dashboard.topPages.length === 0) && (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No page data yet</p>
            )}
          </div>
        </div>

        {/* Behavior Insights */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Behavior Insights</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-900/40 rounded-xl">
              <FaChartBar className="text-4xl text-purple-600 dark:text-purple-400 mb-4" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Pages per Session</h3>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {((dashboard?.today?.pageViews || 0) / Math.max(dashboard?.today?.visitors || 1, 1)).toFixed(2)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Average pages viewed</p>
            </div>

            <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/40 rounded-xl">
              <FaMousePointer className="text-4xl text-green-600 dark:text-green-400 mb-4" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Engagement Rate</h3>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                {Math.min(100, Math.round((dashboard?.today?.avgDuration || 0) / 3))}%
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Based on time spent</p>
            </div>

            <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/40 rounded-xl">
              <FaClock className="text-4xl text-blue-600 dark:text-blue-400 mb-4" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Return Rate</h3>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {Math.round((stats?.liveVisitors || 0) / Math.max(dashboard?.today?.visitors || 1, 1) * 100)}%
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Returning visitors</p>
            </div>
          </div>
        </div>

        {/* Daily Trend Chart */}
        {dashboard?.dailyTrend && dashboard.dailyTrend.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
              <FaChartLine className="text-blue-600" />
              30-Day Trend
            </h2>
            <div className="space-y-2">
              {dashboard.dailyTrend.slice(-7).map((day, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400 w-24">
                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-8 relative">
                    <div 
                      className="bg-gradient-to-r from-blue-600 to-purple-600 h-8 rounded-full flex items-center justify-end pr-3 text-white text-sm font-bold transition-all"
                      style={{ width: `${(day.visitors / Math.max(...dashboard.dailyTrend.map(d => d.visitors))) * 100}%` }}
                    >
                      {day.visitors}
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 w-16 text-right">{day.pageViews} views</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
